import { DocumentNode, useLazyQuery, useMutation } from '@apollo/client'
import {
    BackendProcessStatus,
    StartBackendProcessResult,
} from 'api/graphQL/types/backendProcess.types'
import ProgressDialog from 'components/ToMoveToSCS/ProgressDialog'
import React from 'react'

/**
 * Properties for the {@link PollableBackendProcessProgressDialog} component.
 * @template TStartMutationInput - The type of the input parameter for the mutation that starts the process. Passed to the "input" variable of the mutation.
 * @template TStartMutationResponse - The type of the response for the mutation that starts the process.
 * @template KStartMutationResponseData - The type of the key in the start mutation response that contains {@link StartBackendProcessResult} with the `instanceId`.
 * @template TStatusQueryResponse - The type of the response to the query for checking the process status.
 * @template TStatus - The type of the status data contained in the status query response.
 * @template KStatus - The type of the key in the status query response data that contains the status.
 * @template TResult - The type of the result of the process when it completes successfully.
 * @template KResult - The type of the key in the status for the result.
 */
interface PollableBackendProcessProgressDialogProps<
    TStartMutationInput,
    TStartMutationResponse,
    KStartMutationResponseData extends keyof TStartMutationResponse,
    TStatusQueryResponse,
    TStatus extends BackendProcessStatus,
    KStatus extends keyof TStatusQueryResponse,
    TResult,
    KResult extends keyof TStatus
> {
    /** ID to give the dialog component. */
    dialogId: string

    /** Title to give the dialog component. */
    dialogTitle: string

    /** Text to display in the dialog (e.g., "Adding the selected components to your project...") */
    dialogText: string

    /** API mutation called to start the process. It should take
     * a parameter named `input` of type {@link TStartMutationInput} and return a response of
     * type {@link TStartMutationResponse} containing a {@link StartBackendProcessResult}.
     */
    startMutation: DocumentNode

    /** Value to assign to the `input` parameter of the start mutation. */
    startMutationInput: Partial<TStartMutationInput>

    /** Key of the property in the start mutation response that contains the instance ID. */
    startMutationResponseKey: KStartMutationResponseData

    /** API query called to check the status of the process. */
    statusQuery: DocumentNode

    /** Key of the property in the status response that contains the status data. */
    statusPropertyKey: KStatus

    /** Key of the property in the status data that contains the result when the process is complete. */
    statusPropertyResultKey: KResult

    /** Called when the process is completed (with or without errors).
     * @param result - The result if there were no errors. `null` if there was an error.
     */
    onCompleted: (result: Partial<TResult>) => void

    /** Called if the API reports an error during the process. */
    onError: (errorMessage: string | null | undefined) => void
}

/**
 * Represents a progress dialog for a pollable backend process.
 * This component is intended to be used for processes that are started by a mutation with an `input` parameter that return a {@link StartBackendProcessResult},
 * and then polled for status by a query that returns a {@link BackendProcessStatus}.
 * It calls the appropriate callbacks when the process completes or errors out.
 *
 * @template TStartMutationInput - The type of the input parameter for the mutation that starts the process. Passed to the "input" variable of the mutation.
 * @template TStartMutationResponse - The type of the response for the mutation that starts the process.
 * @template KStartResponseData - The type of the key in the start mutation response that contains {@link StartBackendProcessResult} with the `instanceId`.
 * For example, in the response:
 * ```json
 * {
 *  "data": {
 *     "projectBulkAdd": {
 *      "instanceId": "b2a0a0a0-0000-0000-0000-000000000000"
 *    }
 * }
 * ```
 * the value of this type parameter would be "projectBulkAdd".
 * @template TStatusQueryResponse - The type of the response to the query for checking the process status.
 * @template TStatus - The type of the status data contained in the status query response.
 * @template KStatus - The type of the key in the status data that contains the result when the process is complete.
 * For example, in the response:
 * ```json
 * {
 *   "data": {
 *      "getProjectBulkAddStatus": {
 *        "state": "COMPLETE",
 *        "result": {...}
 *      }
 *   }
 * }
 * ```
 * the value of this type parameter would be "getProjectBulkAddStatus".
 * @template TResult - The type of the result of the process when it completes successfully.
 * @template KResult - The type of the key in the status for the result.
 * For example, in the response:
 * ```json
 * {
 *   "data": {
 *      "getProjectBulkAddStatus": {
 *      "state": "COMPLETE",
 *      "result": {
 *        "addedElements": [ {
 *          "id": "b2a0a0a0-0000-0000-0000-000000000000",
 *        }
 *      ]
 *     }
 *   }
 * }
 * ```
 * the value of this type parameter would be "result".
 */
const PollableBackendProcessProgressDialog = <
    TStartMutationInput,
    TStartMutationResponse,
    KStartResponseData extends keyof TStartMutationResponse,
    TStatusQueryResponse,
    TStatus extends BackendProcessStatus,
    KStatus extends keyof TStatusQueryResponse,
    TResult,
    KResult extends keyof TStatus
>({
    dialogId,
    dialogTitle,
    dialogText,
    startMutationInput,
    startMutation,
    startMutationResponseKey,
    statusQuery,
    onCompleted,
    statusPropertyKey,
    statusPropertyResultKey,
    onError,
}: PollableBackendProcessProgressDialogProps<
    TStartMutationInput,
    TStartMutationResponse,
    KStartResponseData,
    TStatusQueryResponse,
    TStatus,
    KStatus,
    TResult,
    KResult
>) => {
    const [instanceId, setInstanceId] = React.useState<string>()
    const [progress, setProgress] = React.useState<number>(0)
    const [isInProgress, setIsInProgress] = React.useState<boolean>(false)

    const onStatusQueryCompleted = React.useCallback(
        (data: TStatusQueryResponse) => {
            let status = (data?.[statusPropertyKey] as unknown) as TStatus // TODO is there a better way to avoid the TS error here?
            const processState = status?.state?.toUpperCase()
            switch (processState) {
                case 'INPROGRESS':
                    const progressPercentage = status?.progressPercentage
                    if (
                        progressPercentage !== undefined &&
                        progressPercentage !== progress
                    ) {
                        setProgress(progressPercentage)
                    }
                    // still in progress; poll again after a short time
                    const queryTimeout = setTimeout(() => {
                        queryForStatus()
                        clearTimeout(queryTimeout)
                    }, 1000)
                    break
                case 'COMPLETE':
                    setProgress(100)
                    setIsInProgress(false)
                    const result = status?.[statusPropertyResultKey]
                    if (result) {
                        // To make sure user sees full progress bar, wait a split second before calling
                        // the completion handler.
                        // This was the only way I could find to ensure the "full progress bar" animation
                        // shows before the dialog is closed. If we call the callback directly,
                        // it ends up closing the dialog before the progress bar updates,
                        // whether we use a selective render or set the "open" prop on the progress dialog.
                        const progressBarTimeout = setTimeout(() => {
                            onCompleted(result)
                            clearTimeout(progressBarTimeout)
                        }, 500)
                    }
                    break
                case 'ERROR':
                    setIsInProgress(false)
                    handleError(status?.errorMessage)
                    break
            }
        },
        [startMutationInput, progress, onCompleted, onError]
    )
    const queryForStatus = React.useCallback(() => {
        getStatus()
    }, [instanceId])

    const [getStatus] = useLazyQuery<TStatusQueryResponse>(statusQuery, {
        variables: {
            input: { instanceId: instanceId },
        },
        fetchPolicy: 'no-cache',
        onCompleted: (data) => onStatusQueryCompleted(data),
        onError: (error) => {
            handleError(error.message)
        },
    })

    const [startProcess] = useMutation<
        TStartMutationResponse,
        {
            input: Partial<TStartMutationInput>
        }
    >(startMutation, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            let startResult = (data?.[
                startMutationResponseKey
            ] as unknown) as StartBackendProcessResult // TODO find typesafe way to do this
            let startInstanceId = startResult?.instanceId
            if (startInstanceId) {
                setInstanceId(startInstanceId)
            } else {
                // No instance ID returned by mutation, so we can't proceed;
                handleError(
                    'Server returned an invalid response; cancelling process.'
                )
            }
        },
        onError: (error) => handleError(error.message),
    })

    const handleError = React.useCallback(
        (errorMessage: string | null | undefined) => {
            onError?.(errorMessage)
        },
        [onError]
    )

    React.useEffect(() => {
        if (startMutationInput && !instanceId && !isInProgress) {
            // Call mutation to fire off the process and get an instance ID we can poll on.
            setIsInProgress(true)
            startProcess({
                variables: {
                    input: startMutationInput,
                },
            })
            return
        }

        if (instanceId && isInProgress) {
            queryForStatus()
        }
    }, [startMutationInput, queryForStatus, isInProgress, instanceId])

    return (
        <ProgressDialog
            dialogId={dialogId}
            dialogName={dialogTitle}
            open={true}
            progressPercentage={progress > 100 ? 100 : progress}
            statusText={dialogText}
        />
    )
}

export default PollableBackendProcessProgressDialog
